import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import EventEditDialog from './EventEditDialog';
import EventDeleteDialog from './EventDeleteDialog';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface Event {
  id: string;
  name: string;
  date: string; // ISO datetime string
  venue?: string;
  venue_id?: string;
  ticketTypeName?: string;
}

interface EventCalendarViewProps {
  events: Event[];
  onAddEvent: (date: Dayjs) => void;
  onEventClick: (event: Event) => void;
  onEventUpdated?: () => void;
  productionId: string;
}

function CustomDay(props: PickersDayProps & { highlightedDays?: string[] }) {
  const { highlightedDays = [], day, outsideCurrentMonth } = props;

  const isHighlighted = !outsideCurrentMonth && highlightedDays.some(
    (highlightedDate) => dayjs(highlightedDate).isSame(day, 'day')
  );

  if (outsideCurrentMonth) {
    return <PickersDay {...props} />;
  }

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={isHighlighted ? '•' : undefined}
      color="primary"
    >
      <PickersDay {...props} />
    </Badge>
  );
}

const EventCalendarView: React.FC<EventCalendarViewProps> = ({
  events,
  onAddEvent,
  onEventClick,
  onEventUpdated,
  productionId
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [multipleEventsDialogOpen, setMultipleEventsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Get all event dates for highlighting
  const highlightedDays = events.map(event => event.date);

  // Get events for selected date
  const eventsForSelectedDate = selectedDate
    ? events.filter(event =>
        dayjs(event.date).isSame(selectedDate, 'day')
      )
    : [];

  const handleDateChange = (newDate: Dayjs | Date | null) => {
    setSelectedDate(newDate ? dayjs(newDate) : null);
  };

  const handleAddEvent = () => {
    if (selectedDate) {
      onAddEvent(selectedDate);
    }
  };

  const handleEventItemClick = (event: Event) => {
    if (eventsForSelectedDate.length > 1) {
      setMultipleEventsDialogOpen(false);
    }
    onEventClick(event);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Calendar */}
        <Paper elevation={0} sx={{ flex: 1, p: 2, border: '1px solid', borderColor: 'divider' }}>
          <DateCalendar
            value={selectedDate}
            onChange={handleDateChange}
            slots={{
              day: CustomDay
            }}
            slotProps={{
              day: {
                highlightedDays
              } as any
            }}
          />
        </Paper>

        {/* Events for Selected Date */}
        <Paper elevation={0} sx={{ flex: 1, p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedDate ? selectedDate.format('DD. MMMM YYYY') : 'Izaberite datum'}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddEvent}
              disabled={!selectedDate}
              variant="contained"
            >
              Dodaj
            </Button>
          </Box>

          {eventsForSelectedDate.length > 0 ? (
            <List sx={{ py: 0 }}>
              {eventsForSelectedDate.map((event) => {
                // Extract time from event date
                const eventTime = dayjs(event.date).format('HH:mm');

                return (
                  <ListItem
                    key={event.id}
                    sx={{
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        '& .event-actions': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <EventIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Box
                        sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={() => handleEventItemClick(event)}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {eventTime}
                        </Typography>
                        {event.venue && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {event.venue}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        className="event-actions"
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          opacity: 0,
                          transition: 'opacity 0.2s'
                        }}
                      >
                        <Tooltip title="Izmeni">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEvent(event);
                              setEditDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Obriši">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEvent(event);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Nema događaja za izabrani datum
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Kliknite "Dodaj" da kreirate novi događaj
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Multiple Events Dialog */}
      <Dialog
        open={multipleEventsDialogOpen}
        onClose={() => setMultipleEventsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Događaji za {selectedDate?.format('DD. MMMM YYYY')}
        </DialogTitle>
        <DialogContent>
          <List sx={{ py: 0 }}>
            {eventsForSelectedDate.map((event) => {
              const eventTime = dayjs(event.date).format('HH:mm');

              return (
                <ListItem
                  key={event.id}
                  onClick={() => handleEventItemClick(event)}
                  sx={{
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <EventIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {eventTime}
                      </Typography>
                      {event.venue && (
                        <Typography variant="caption" color="text.secondary">
                          {event.venue}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMultipleEventsDialogOpen(false)}>
            Zatvori
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Edit Dialog */}
      <EventEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedEvent(null);
        }}
        onSuccess={() => {
          onEventUpdated?.();
        }}
        event={selectedEvent ? {
          id: selectedEvent.id,
          name: selectedEvent.name,
          event_date: selectedEvent.date,
          venue_name: selectedEvent.venue,
          venue_id: selectedEvent.venue_id
        } : null}
        productionId={productionId}
      />

      {/* Event Delete Dialog */}
      <EventDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedEvent(null);
        }}
        onSuccess={() => {
          onEventUpdated?.();
        }}
        event={selectedEvent ? {
          id: selectedEvent.id,
          name: selectedEvent.name,
          event_date: selectedEvent.date,
          venue_name: selectedEvent.venue,
          venue_id: selectedEvent.venue_id
        } : null}
        productionId={productionId}
      />
    </LocalizationProvider>
  );
};

export default EventCalendarView;
